import { useState, useEffect } from 'react';
import { mainSiteUrl, categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    if (mobileOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileOpen
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
            {/* Logo */}
            <a
              href="/"
              onClick={closeMobile}
              className={`flex items-center gap-2 whitespace-nowrap ${
                scrolled || mobileOpen ? 'text-foreground-900' : 'text-white'
              }`}
            >
              <span className="font-heading text-lg md:text-xl font-semibold tracking-tight">
                cssspreadsheet<span className="text-accent-400">.bond</span>
              </span>
            </a>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-3">
              <a
                href="/article/cssbuy-spreadsheet-guide"
                className={`text-sm font-medium whitespace-nowrap transition-colors ${
                  scrolled
                    ? 'text-foreground-600 hover:text-foreground-900'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                Ultimate Guide
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-full whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                cssbuy sheet
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-xs"></i></span>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-5 py-2 bg-accent-500 text-white text-sm font-medium rounded-full whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
              >
                buy it now
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-xs"></i></span>
              </a>
            </div>

            {/* Mobile: hamburger + quick CTA */}
            <div className="flex md:hidden items-center gap-2">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-500 text-white text-xs font-medium rounded-full whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                buy now
              </a>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                  scrolled || mobileOpen
                    ? 'text-foreground-700 hover:bg-background-200'
                    : 'text-white hover:bg-white/15'
                }`}
              >
                {mobileOpen ? (
                  <i className="ri-close-line text-xl"></i>
                ) : (
                  <i className="ri-menu-line text-xl"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile slide-out drawer */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          mobileOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          onClick={closeMobile}
          className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
            mobileOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {/* Drawer panel */}
        <div
          className={`absolute top-0 right-0 h-full w-[85vw] max-w-[380px] bg-background-50 shadow-lg flex flex-col transition-transform duration-300 ease-out ${
            mobileOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Drawer header */}
          <div className="flex items-center justify-between px-5 h-16 border-b border-background-200/60 shrink-0">
            <span className="font-heading text-base font-semibold text-foreground-900">Menu</span>
            <button
              onClick={closeMobile}
              aria-label="Close menu"
              className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-200 hover:text-foreground-800 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Drawer content — scrollable */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
            {/* Guide link */}
            <a
              href="/article/cssbuy-spreadsheet-guide"
              onClick={closeMobile}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-foreground-900 text-white text-sm font-medium rounded-lg hover:bg-foreground-800 transition-colors cursor-pointer w-full justify-center whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-article-line"></i></span>
              Ultimate Guide
            </a>

            {/* Categories */}
            <div>
              <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 whitespace-nowrap">Categories</h3>
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobile}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap group"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-hover:text-primary-500 transition-colors">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Drawer footer — CTA buttons */}
          <div className="px-5 py-4 border-t border-background-200/60 space-y-2.5 shrink-0">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              onClick={closeMobile}
              className="flex items-center justify-center gap-2 w-full py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              cssbuy sheet
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-xs"></i></span>
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              onClick={closeMobile}
              className="flex items-center justify-center gap-2 w-full py-3 bg-accent-500 text-white text-sm font-medium rounded-full hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              buy it now
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-xs"></i></span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}